// =============================================================================
// EVOLUTION GLOBALE CHART - Courbes agrégées tous chantiers
// =============================================================================

import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Info, TrendingUp, TrendingDown } from 'lucide-react';
import {
  CHART_SERIES_COLORS,
  formatMontantChart,
  tooltipContainerStyle,
} from '@/components/charts/chartConfig';

interface Props {
  totaux: {
    revenus: number;
    couts: number;
    marge: number;
    rentabiliteMoyenne: number;
    nombreChantiers: number;
  };
}

// =============================================================================
// TOOLTIP PERSONNALISÉ
// =============================================================================

function CustomTooltip({ active, payload, label }: any) {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={tooltipContainerStyle} className="min-w-[250px]">
      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/10">
        <span className="text-sm font-medium text-white/90">{label}</span>
      </div>

      {payload.map((entry: any, index: number) => (
        <div key={index} className="flex items-start gap-3 mb-2">
          <div
            className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
            style={{ backgroundColor: entry.color }}
          />
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">{entry.name}</span>
              <span className="text-lg font-bold text-white">
                {formatMontantChart(entry.value)}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// LÉGENDE PERSONNALISÉE
// =============================================================================

function CustomLegend() {
  const items = [
    { name: 'Revenus totaux', color: CHART_SERIES_COLORS.serie2 },
    { name: 'Coûts totaux', color: CHART_SERIES_COLORS.serie1 },
    { name: 'Marge globale', color: CHART_SERIES_COLORS.serie4 },
  ];

  return (
    <div className="flex flex-wrap gap-6 mt-4 justify-center">
      {items.map((item) => (
        <div key={item.name} className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-sm"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-sm text-muted-foreground">{item.name}</span>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// COMPOSANT PRINCIPAL
// =============================================================================

export function EvolutionGlobaleChart({ totaux }: Props) {
  // Créer des données synthétiques pour l'affichage
  // (Dans une vraie implémentation, on chargerait l'historique depuis chantier_snapshots)
  const data = useMemo(() => {
    const { revenus, couts, marge } = totaux;

    // Générer 6 points pour simulation visuelle
    return [
      { label: 'Début', revenus: revenus * 0.1, couts: couts * 0.05, marge: marge * 0.1 },
      { label: 'S1', revenus: revenus * 0.25, couts: couts * 0.2, marge: marge * 0.2 },
      { label: 'S2', revenus: revenus * 0.45, couts: couts * 0.4, marge: marge * 0.35 },
      { label: 'S3', revenus: revenus * 0.65, couts: couts * 0.6, marge: marge * 0.55 },
      { label: 'S4', revenus: revenus * 0.85, couts: couts * 0.8, marge: marge * 0.75 },
      { label: 'Actuel', revenus, couts, marge },
    ];
  }, [totaux]);

  // Domaine Y
  const yDomain = useMemo(() => {
    const allValues = data.flatMap((d) => [d.revenus, d.couts, d.marge]);
    const min = Math.min(...allValues, 0);
    const max = Math.max(...allValues);
    const padding = (max - min) * 0.1 || 10000;
    return [Math.floor((min - padding) / 1000) * 1000, Math.ceil((max + padding) / 1000) * 1000];
  }, [data]);

  if (totaux.nombreChantiers === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-muted-foreground">
        <Info className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-sm">Aucun chantier à analyser</p>
        <p className="text-xs mt-1 opacity-70">Les données apparaîtront avec des chantiers actifs</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* KPIs en haut - montants exacts sans arrondi */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Revenus totaux</p>
          <p className="text-xl font-bold text-foreground">
            {Math.round(totaux.revenus).toLocaleString('fr-FR')} €
          </p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Coûts totaux</p>
          <p className="text-xl font-bold" style={{ color: CHART_SERIES_COLORS.serie1 }}>
            {Math.round(totaux.couts).toLocaleString('fr-FR')} €
          </p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Marge globale</p>
          <p className={`text-xl font-bold ${totaux.marge >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
            {Math.round(totaux.marge).toLocaleString('fr-FR')} €
          </p>
          <div className="flex items-center gap-1 mt-1">
            {totaux.rentabiliteMoyenne >= 0 ? (
              <TrendingUp className="h-3 w-3 text-emerald-400" />
            ) : (
              <TrendingDown className="h-3 w-3 text-rose-400" />
            )}
            <span className="text-xs text-muted-foreground">
              {totaux.rentabiliteMoyenne.toFixed(1)}% moy.
            </span>
          </div>
        </div>
      </div>

      {/* Graphique */}
      <div className="flex-1 min-h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
            <defs>
              <linearGradient id="gradientRevenus" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_SERIES_COLORS.serie2} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_SERIES_COLORS.serie2} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradientCouts" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_SERIES_COLORS.serie1} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_SERIES_COLORS.serie1} stopOpacity={0} />
              </linearGradient>
              <linearGradient id="gradientMarge" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={CHART_SERIES_COLORS.serie4} stopOpacity={0.4} />
                <stop offset="100%" stopColor={CHART_SERIES_COLORS.serie4} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              stroke="hsl(0 0% 25%)"
              strokeOpacity={0.3}
              vertical={false}
            />

            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(0 0% 60%)', fontSize: 12 }}
              dy={10}
            />
            <YAxis
              domain={yDomain}
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(0 0% 60%)', fontSize: 12 }}
              tickFormatter={(value) => formatMontantChart(value)}
              width={70}
            />

            <Tooltip content={<CustomTooltip />} />

            <Area
              type="monotone"
              dataKey="revenus"
              name="Revenus totaux"
              stroke={CHART_SERIES_COLORS.serie2}
              strokeWidth={2}
              fill="url(#gradientRevenus)"
              dot={false}
              animationDuration={1500}
            />

            <Area
              type="monotone"
              dataKey="couts"
              name="Coûts totaux"
              stroke={CHART_SERIES_COLORS.serie1}
              strokeWidth={2}
              fill="url(#gradientCouts)"
              dot={false}
              animationDuration={1500}
            />

            <Area
              type="monotone"
              dataKey="marge"
              name="Marge globale"
              stroke={CHART_SERIES_COLORS.serie4}
              strokeWidth={3}
              fill="url(#gradientMarge)"
              dot={false}
              animationDuration={1500}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <CustomLegend />

      {/* Info bulle */}
      <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/10">
        <Info className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          Vue d'ensemble de {totaux.nombreChantiers} chantier{totaux.nombreChantiers > 1 ? 's' : ''}.
          La marge globale représente la différence entre vos revenus (devis acceptés) et vos coûts réels engagés.
        </p>
      </div>
    </div>
  );
}
